import React, { useState, useEffect, useCallback } from 'react';
import { fastapiService } from '../../services/fastapiService';

interface QueryRecord {
  ts: string;
  table: string;
  method: string;
  filters: string;
  duration_ms: number;
  caller: string;
  source?: string;
  error: string | null;
}

interface QueryStat {
  count: number;
  total_ms: number;
  avg_ms: number;
  errors: number;
  by_source?: Record<string, number>;
}

interface Summary {
  total_queries: number;
  unique_tables: number;
  total_errors: number;
  avg_duration_ms: number;
  buffer_capacity: number;
  by_source?: Record<string, number>;
}

type Tab = 'overview' | 'queries' | 'slow' | 'errors';

export const SupabaseMonitorPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('overview');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [stats, setStats] = useState<Record<string, QueryStat>>({});
  const [queries, setQueries] = useState<QueryRecord[]>([]);
  const [slowQueries, setSlowQueries] = useState<QueryRecord[]>([]);
  const [errorQueries, setErrorQueries] = useState<QueryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableFilter, setTableFilter] = useState('');
  const [minDuration, setMinDuration] = useState(0);
  const [slowThreshold, setSlowThreshold] = useState(500);
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumResp, statsResp] = await Promise.all([
        fastapiService.request('/admin/supabase-monitor/summary'),
        fastapiService.request('/admin/supabase-monitor/stats'),
      ]);
      setSummary(sumResp?.data || null);
      setStats(statsResp?.data || {});

      const sourceParam = sourceFilter ? { source: sourceFilter } : {};
      if (tab === 'queries') {
        const params: Record<string, any> = { limit: 200, ...sourceParam };
        if (tableFilter) params.table = tableFilter;
        if (minDuration > 0) params.min_duration_ms = minDuration;
        const qResp = await fastapiService.request('/admin/supabase-monitor/queries', { params });
        setQueries(qResp?.data || []);
      } else if (tab === 'slow') {
        const sResp = await fastapiService.request('/admin/supabase-monitor/slow', {
          params: { threshold_ms: slowThreshold, limit: 100, ...sourceParam },
        });
        setSlowQueries(sResp?.data || []);
      } else if (tab === 'errors') {
        const eResp = await fastapiService.request('/admin/supabase-monitor/errors', {
          params: { limit: 100, ...sourceParam },
        });
        setErrorQueries(eResp?.data || []);
      }
    } catch (err) {
      console.error('Supabase monitor fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [tab, tableFilter, minDuration, slowThreshold, sourceFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchAll, 30000); // 30s to reduce DB load (was 5s)
    return () => clearInterval(id);
  }, [autoRefresh, fetchAll]);

  const handleReset = async () => {
    if (!confirm('Clear all tracked query data?')) return;
    await fastapiService.request('/admin/supabase-monitor/reset', { method: 'POST' });
    fetchAll();
  };

  const formatTs = (ts: string) => {
    try {
      return new Date(ts).toLocaleTimeString();
    } catch {
      return ts;
    }
  };

  const durationColor = (ms: number) => {
    if (ms >= 1000) return 'text-red-600 font-semibold';
    if (ms >= 500) return 'text-orange-500';
    if (ms >= 200) return 'text-yellow-600';
    return 'text-green-600';
  };

  const methodBadge = (method: string) => {
    const colors: Record<string, string> = {
      select: 'bg-blue-100 text-blue-700',
      insert: 'bg-green-100 text-green-700',
      update: 'bg-yellow-100 text-yellow-700',
      upsert: 'bg-purple-100 text-purple-700',
      delete: 'bg-red-100 text-red-700',
      rpc: 'bg-indigo-100 text-indigo-700',
    };
    return colors[method] || 'bg-gray-100 text-gray-700';
  };

  const sourceBadge = (source: string | undefined) => {
    const s = source || 'FastAPI';
    const colors: Record<string, string> = {
      FastAPI: 'bg-cyan-100 text-cyan-700',
      Backend: 'bg-amber-100 text-amber-700',
      Frontend: 'bg-emerald-100 text-emerald-700',
    };
    return colors[s] || 'bg-gray-100 text-gray-700';
  };

  const renderQueryTable = (rows: QueryRecord[]) => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
            <th className="px-3 py-3">Time</th>
            <th className="px-3 py-3">Table</th>
            <th className="px-3 py-3">Method</th>
            <th className="px-3 py-3">Source</th>
            <th className="px-3 py-3 text-right">Duration</th>
            <th className="px-3 py-3">Caller</th>
            <th className="px-3 py-3">Filters</th>
            <th className="px-3 py-3">Error</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((q, i) => (
            <tr key={i} className={`hover:bg-gray-50 ${q.error ? 'bg-red-50' : ''}`}>
              <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{formatTs(q.ts)}</td>
              <td className="px-3 py-2 font-mono text-xs font-medium">{q.table}</td>
              <td className="px-3 py-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${methodBadge(q.method)}`}>
                  {q.method}
                </span>
              </td>
              <td className="px-3 py-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sourceBadge(q.source)}`}>
                  {q.source || 'FastAPI'}
                </span>
              </td>
              <td className={`px-3 py-2 text-right font-mono text-xs ${durationColor(q.duration_ms)}`}>
                {q.duration_ms}ms
              </td>
              <td className="px-3 py-2 font-mono text-xs text-gray-600 max-w-xs truncate" title={q.caller}>
                {q.caller}
              </td>
              <td className="px-3 py-2 text-xs text-gray-500 max-w-xs truncate" title={q.filters}>
                {q.filters || '—'}
              </td>
              <td className="px-3 py-2 text-xs text-red-600 max-w-xs truncate" title={q.error || ''}>
                {q.error || '—'}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                No queries found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'queries', label: 'Recent Queries' },
    { key: 'slow', label: 'Slow Queries' },
    { key: 'errors', label: 'Errors' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Supabase Query Monitor</h1>
          <p className="text-sm text-gray-500 mt-1">
            Real-time tracking of Supabase calls from FastAPI, Backend, or Frontend
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={fetchAll}
            disabled={loading}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            onClick={handleReset}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Total Queries', value: summary.total_queries.toLocaleString(), color: 'text-blue-700' },
              { label: 'Unique Tables', value: summary.unique_tables, color: 'text-purple-700' },
              { label: 'Errors', value: summary.total_errors, color: summary.total_errors > 0 ? 'text-red-600' : 'text-green-600' },
              { label: 'Avg Duration', value: `${summary.avg_duration_ms}ms`, color: durationColor(summary.avg_duration_ms) },
              { label: 'Buffer', value: `${summary.total_queries}/${summary.buffer_capacity}`, color: 'text-gray-600' },
            ].map((card, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide">{card.label}</p>
                <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>
          {summary.by_source && Object.keys(summary.by_source).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">By Source</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(summary.by_source).map(([src, count]) => (
                  <span key={src} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                    {src}: {count.toLocaleString()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              {t.key === 'errors' && summary && summary.total_errors > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">
                  {summary.total_errors}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {tab === 'overview' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold">Query Stats by Table &amp; Method</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3">Table.Method</th>
                  <th className="px-4 py-3 text-right">Calls</th>
                  <th className="px-4 py-3">By Source</th>
                  <th className="px-4 py-3 text-right">Total ms</th>
                  <th className="px-4 py-3 text-right">Avg ms</th>
                  <th className="px-4 py-3 text-right">Errors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(stats).map(([key, s]) => (
                  <tr key={key} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{key}</td>
                    <td className="px-4 py-2 text-right font-semibold">{s.count.toLocaleString()}</td>
                    <td className="px-4 py-2">
                      {s.by_source && Object.keys(s.by_source).length > 0 ? (
                        <span className="text-xs text-gray-600">
                          {Object.entries(s.by_source)
                            .map(([src, n]) => `${src}: ${n}`)
                            .join(', ')}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">{s.total_ms.toLocaleString()}</td>
                    <td className={`px-4 py-2 text-right ${durationColor(s.avg_ms)}`}>{s.avg_ms}</td>
                    <td className={`px-4 py-2 text-right ${s.errors > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                      {s.errors}
                    </td>
                  </tr>
                ))}
                {Object.keys(stats).length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      No queries tracked yet. Perform some actions and refresh.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'queries' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Filter by table</label>
              <input
                type="text"
                placeholder="e.g. contacts"
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-48"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Source</label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-32"
              >
                <option value="">All</option>
                <option value="FastAPI">FastAPI</option>
                <option value="Backend">Backend</option>
                <option value="Frontend">Frontend</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Min duration (ms)</label>
              <input
                type="number"
                value={minDuration}
                onChange={(e) => setMinDuration(Number(e.target.value))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-28"
              />
            </div>
            <button
              onClick={fetchAll}
              className="px-3 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Apply
            </button>
          </div>
          {renderQueryTable(queries)}
        </div>
      )}

      {tab === 'slow' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Threshold (ms)</label>
              <input
                type="number"
                value={slowThreshold}
                onChange={(e) => setSlowThreshold(Number(e.target.value))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-28"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Source</label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-32"
              >
                <option value="">All</option>
                <option value="FastAPI">FastAPI</option>
                <option value="Backend">Backend</option>
                <option value="Frontend">Frontend</option>
              </select>
            </div>
            <button
              onClick={fetchAll}
              className="px-3 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Apply
            </button>
          </div>
          {renderQueryTable(slowQueries)}
        </div>
      )}

      {tab === 'errors' && (
        <div className="space-y-4">
          <div className="flex gap-4 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Source</label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-32"
              >
                <option value="">All</option>
                <option value="FastAPI">FastAPI</option>
                <option value="Backend">Backend</option>
                <option value="Frontend">Frontend</option>
              </select>
            </div>
            <button
              onClick={fetchAll}
              className="px-3 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Apply
            </button>
          </div>
          {renderQueryTable(errorQueries)}
        </div>
      )}
    </div>
  );
};
